import moment from 'moment';
import { USER_TIME_FORMAT } from 'shared/constants/date';
import { getShiftTextRepresentation } from 'shared/utils/getShiftTextRepresentation';
import type { Bell } from 'services/api';
import { getBellShiftIndex } from './utils';

export type ParsedBell = {
  bell: Bell;
  index: number;
  start: moment.Moment;
  end: moment.Moment;
};

type ShiftGroupedParsedBells = Map<Bell['shift'], ParsedBell[]>;

const parseAndValidateBaseRules = (
  bells: Bell[],
): { parsedBells?: ParsedBell[]; error?: string } => {
  const parsedBells: ParsedBell[] = [];

  for (let i = 0; i < bells.length; i++) {
    const bell = bells[i];
    if (!bell.startTime || !bell.endTime) {
      return { error: `Заполните время для урока ${getBellShiftIndex(undefined, i)}` };
    }

    const start = moment(bell.startTime, USER_TIME_FORMAT);
    const end = moment(bell.endTime, USER_TIME_FORMAT);
    if (end.isSameOrBefore(start)) {
      return {
        error: `Урок ${getBellShiftIndex(undefined, i)}: конец урока должен быть позже начала`,
      };
    }

    parsedBells.push({ bell, index: i, start, end });
  }

  return { parsedBells };
};

const groupByShift = (parsedBells: ParsedBell[]): ShiftGroupedParsedBells => {
  const bellsByShift: ShiftGroupedParsedBells = new Map<Bell['shift'], ParsedBell[]>();

  parsedBells.forEach((parsedBell) => {
    const list = bellsByShift.get(parsedBell.bell.shift) ?? [];
    list.push(parsedBell);
    bellsByShift.set(parsedBell.bell.shift, list);
  });

  return bellsByShift;
};

const validateInsideShiftOrder = (bellsByShift: ShiftGroupedParsedBells): string | undefined => {
  const shiftGroups = Array.from(bellsByShift.values());

  for (let groupIndex = 0; groupIndex < shiftGroups.length; groupIndex++) {
    const shiftBells = shiftGroups[groupIndex];

    for (let i = 1; i < shiftBells.length; i++) {
      const previous = shiftBells[i - 1];
      const current = shiftBells[i];

      if (current.start.isSameOrBefore(previous.end)) {
        return `${getShiftTextRepresentation(current.bell.shift)}: начало урока ${getBellShiftIndex(
          current,
        )} должно быть позже конца урока ${getBellShiftIndex(previous)}`;
      }
    }
  }

  return undefined;
};

const validateCrossShiftOverlaps = (bellsByShift: ShiftGroupedParsedBells): string | undefined => {
  const shiftGroups = Array.from(bellsByShift.values());
  const left = shiftGroups[0];
  const right = shiftGroups[1];

  let leftPointer = 0;
  let rightPointer = 0;

  while (leftPointer < left.length && rightPointer < right.length) {
    const first = left[leftPointer];
    const second = right[rightPointer];
    const isExactMatch = first.start.isSame(second.start) && first.end.isSame(second.end);
    const hasOverlap = first.start.isBefore(second.end) && second.start.isBefore(first.end);

    if (hasOverlap && !isExactMatch) {
      return `Уроки ${getBellShiftIndex(first)} и ${getBellShiftIndex(
        second,
      )} не должны пересекаться по времени, кроме полного совпадения`;
    }

    if (first.end.isSameOrBefore(second.end)) {
      leftPointer += 1;
    } else {
      rightPointer += 1;
    }
  }

  return undefined;
};

export const validateBellScheduleBells = (bells: Bell[]): string | undefined => {
  if (!bells.length) {
    return 'Заполните время для всех уроков';
  }

  const parseResult = parseAndValidateBaseRules(bells);
  if (parseResult.error) {
    return parseResult.error;
  }

  const bellsByShift = groupByShift(parseResult.parsedBells ?? []);
  const shiftOrderError = validateInsideShiftOrder(bellsByShift);
  if (shiftOrderError) {
    return shiftOrderError;
  }

  return validateCrossShiftOverlaps(bellsByShift);
};
